// Shared
import { getTextWidth } from '../shared/helper';

// Components
import Actions from '../components/listing/Actions/Actions';
import Filter from '../components/listing/Filter/Filter';
import FormInput from '../components/detail/FormInput/FormInput';

// Taxonomies
import { countryCore, countryFilter } from '../taxonomies/country';

// Model core
export function modelCore() {
  const endpointSimple = '/models/simple';
  const endpoint = '/models';
  const endpointDescriptionVersion = '/descriptionversion/type/model/id';
  const detailId = 'modelId';
  const detailHash = 'modelHash';

  const getDetailId = (data = null) => {
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      return data[0][detailId];
    }

    return data[detailId];
  };

  const getDetailHash = (data = null) => {
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      return data[0][detailHash];
    }

    return data[detailHash];
  };

  return {
    endpoint,
    endpointSimple,
    detailId,
    detailHash,
    endpointDescriptionVersion,
    getDetailId: getDetailId,
    getDetailHash: getDetailHash
  };
}

// Model filter
export function modelFilter() {
  const parameter = 'ModelId';
  const parameterForVistaOrder = 'ModelType';

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        value: row.modelId,
        code: row.modelCode
      };

      // Object values
      const objects = {
        label: row.modelDescription
      };

      // Get the text/value from the default culture
      Object.entries(objects).forEach(([j, prop]) => {
        prop.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            rows[i][j] = culture.text;
          }
        });
      });
    });

    return rows;
  };

  // Rows for vista order export filters has translation as value (XC40, C40...)
  const getRowsForVistaOrder = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        code: row.modelCode
      };

      // Value and label will be the same for vista order
      row.modelDescription.translations.forEach(culture => {
        if (culture.defaultCulture === true) {
          rows[i]['value'] = culture.text;
          rows[i]['label'] = culture.text;
        }
      });
    });

    return rows;
  };

  return {
    parameter,
    parameterForVistaOrder,
    getRows: getRows,
    getRowsForVistaOrder: getRowsForVistaOrder
  };
}

// Model listing
export function modelListing() {
  // Initial sort
  const initialSort = { field: 'id', sort: 'asc' };

  // Initial filter values
  const initialFilters = {};

  // Get columns
  // columnsCustomized is used in case there is a hidden column
  const getColumns = (rows, columnsCustomized) => {
    const columns =
      columnsCustomized.length > 0
        ? columnsCustomized
        : [
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: function (params) {
                return (
                  <Actions
                    typeOfData="taxonomy"
                    id={params.row.id}
                    hash={params.row.hash}
                    endpoint="/model"
                    deleteApiEndpoint="/models"
                  />
                );
              },
              hideFromColumnsSelector: true
            },
            // DataGrid requires the "id" column to be unique,
            // so this is a unique/fake ID required by DataGrid
            {
              field: 'id',
              headerName: 'Unique ID',
              hide: true,
              hideFromColumnsSelector: true
            },
            // DataGrid requires the "id" column to be unique,
            // so this is the real ID to show on the columns for the user
            {
              field: 'realId',
              headerName: 'ID',
              hide: true
            },
            {
              field: 'hash',
              headerName: 'Hash',
              hide: true,
              hideFromColumnsSelector: true
            },
            { field: 'enabled', headerName: 'Enabled' },
            { field: 'country', headerName: 'Country' },
            { field: 'code', headerName: 'Code' },
            { field: 'description', headerName: 'Description' }
          ];

    const columnsResized = columns;

    columns.forEach((column, i) => {
      const id = column.field;
      let text = column.headerName;

      const textWidth = getTextWidth(text);
      columnsResized[i].width = textWidth;

      rows.forEach((row, j) => {
        if (row[id]) {
          const text = row[id];
          const textWidth = getTextWidth(text);

          if (columnsResized[i].width < textWidth) {
            columnsResized[i].width = textWidth;
          }
        }
      });
    });

    return columnsResized;
  };

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        id: i,
        realId: row.modelId,
        hash: row.modelHash,
        enabled: row.modelEnabled ? 'Yes' : 'No',
        country: row.countryName,
        code: row.modelCode
      };

      // Object values
      const objects = {
        description: row.modelDescription
      };

      // Get the text/value from the default culture
      Object.entries(objects).forEach(([j, prop]) => {
        prop.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            rows[i][j] = culture.text;
          }
        });
      });
    });

    return rows;
  };

  // Get filters
  const getFilters = () => {
    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    return [
      [
        {
          size: { xs: 12 },
          component: (
            <Filter
              type="select"
              parameter={countryFilter_.parameter.toLowerCase()}
              label="Country"
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
            />
          )
        }
      ]
    ];
  };

  return {
    initialSort,
    initialFilters,
    getColumns: getColumns,
    getRows: getRows,
    getFilters: getFilters
  };
}

// Model detail
export function modelDetail() {
  const baseUrl = '/model';

  const getInputs = (data = null, prePopulate = false) => {
    // set an object of all the field values
    // where the default state is empty for each
    let detailObj = {
      modelId: '',
      modelHash: '',
      modelCode: '',
      modelDescription: {},
      modelDescriptionText: '',
      modelEnabled: false,
      countryId: ''
    };

    if (data !== null) {
      // Use the data provided to populate the default value of the fields
      Object.entries(detailObj)
        .filter(([i, prop]) => {
          // we cannot rely on a truthy check here,
          // b/c the minor version values can be a zero
          return data[i] != null
        })
        .forEach(([i, prop]) => {
          detailObj[i] = data[i];
        });
      // Get the text/value from the default culture
      if (
        detailObj.modelDescription &&
        detailObj.modelDescription.translations
      ) {
        detailObj.modelDescription.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            detailObj.modelDescriptionText = culture.text;
          }
        });
      }
    }

    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const modelCore_ = modelCore();

    return [
      // Data Version (description.MajorVersion + description.MinorVersion)
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          className: "version-item-wrapper",
          component: (
            <FormInput
              type="version-control"
              inputProps={{
                itemType: "taxonomy",
                versionType: "description",
                versionLabel: "Version",
                endPoint: modelCore_.endpointDescriptionVersion,
                itemId: detailObj.modelId,
                majorVersion: detailObj.modelDescription.majorVersion,
                minorVersion: detailObj.modelDescription.minorVersion,
                dialogueTitle: "Data Version",
                ctaLabel: "Edit version"
              }}
            />
          )
        }
      ],
      // Country
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter={countryCore_.detailId}
              label="Country"
              required={true}
              disabled={prePopulate ? true : false}
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
              inputValue={detailObj.countryId}
              isCulturesSource={prePopulate ? false : true}
              dispatchEventOnChange={prePopulate ? false : true}
            />
          )
        }
      ],
      // Model Code
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="modelCode"
              label="Code"
              required={true}
              inputValue={detailObj.modelCode}
            />
          )
        }
      ],
      // Description
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="localization"
              parameter="modelDescription"
              label="Description"
              required={true}
              inputValue={{
                key: detailObj.modelDescription.key
                  ? detailObj.modelDescription.key
                  : '',
                translations: detailObj.modelDescription.translations
                  ? detailObj.modelDescription.translations
                  : [],
                defaultCultureText: detailObj.modelDescriptionText
              }}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Enabled
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="checkbox"
              label="Enabled"
              parameter="modelEnabled"
              required={true}
              inputValue={detailObj.modelEnabled}
            />
          )
        }
      ]
    ];
  };

  return {
    baseUrl,
    getInputs: getInputs
  };
}
